// components
import {
  Button,
  Cascader,
  Form,
  Image,
  Input,
  message,
  Modal,
  Radio,
  Select,
  Space,
} from 'antd';
import dayjs from 'dayjs';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Webcam from 'react-webcam';

import client from '@/lib/feathers';
import useService from '@/lib/hooks/useService';
import type { DepositRegistration } from '@/types/database/depositRegistration';
import type { ResidentAccountNumber } from '@/types/database/residentAccountNumbers';
import { generateHouseHoldNumberCascader } from '@/utils/generateHouseHoldNumberCascader';
import {
  getResidentsByHouseholdNumber,
  type Resident,
} from '@/utils/getResidentsByHouseholdNumber';

import type { CommunityData } from '../../../types';
import styles from './styles.module.scss';

const { TextArea } = Input;
const { Item, useForm } = Form;

interface EditFormModalProps {
  editType: 'add' | 'edit';
  open: boolean;
  afterEdit: () => void;
  onCancel: () => void;
  editItem?: Partial<Omit<DepositRegistration, 'peopleHousehold'>> & {
    household: string[];
    peopleHousehold: string[];
  };
}

const EditFormModal: React.FC<EditFormModalProps> = ({
  editType,
  open,
  afterEdit,
  onCancel,
  editItem,
}) => {
  const [showPage, setShowPage] = useState<'form' | 'camera'>('form');
  const [videoDevices, setVideoDevices] = useState<
    { label: string; value: string }[]
  >([]);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string | null>(
    null
  );
  const [itemImageSrc, setItemImageSrc] = useState<string | null>(null);
  const [cameraAnimation, setCameraAnimation] = useState<boolean>(false);
  const [depositType, setDepositType] = useState<'寄件' | '收件'>('寄件');
  const [recipientOptions, setRecipientOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [peopleOptions, setPeopleOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const [householdNumbers] = useService<ResidentAccountNumber>({
    collectionName: 'residentAccountNumbers',
  });
  const [communityData] = useService<CommunityData>({
    collectionName: 'communityData',
  });

  const [users] = useService<Resident>({
    collectionName: 'users',
    query: { householdNumbers: { $exists: true } },
  });

  const householdOptions = useMemo(() => {
    const numbers: string[] = [];
    if (householdNumbers) {
      householdNumbers.forEach((number) => {
        if (number.householdNumber) numbers.push(number.householdNumber);
      });
    }
    return generateHouseHoldNumberCascader(numbers);
  }, [householdNumbers]);

  const [form] = useForm<
    Omit<DepositRegistration, 'peopleHousehold'> & {
      household: string[];
      peopleHousehold: string[];
    }
  >();

  const peopleHousehold = Form.useWatch('peopleHousehold', form);

  const webcamRef = useRef<Webcam>(null);

  const takePhoto = () => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      setItemImageSrc(imageSrc);
      setCameraAnimation(true);
    }
  };

  const clearForm = useCallback(() => {
    form.resetFields();
    setDepositType('寄件');
    setItemImageSrc(null);
  }, [form]);

  const handleFinish = async (
    values: Partial<Omit<DepositRegistration, 'peopleHousehold'>> & {
      household: string[];
      peopleHousehold: string[];
    }
  ) => {
    const newDeposit: Partial<DepositRegistration> = {
      ...values,
      household: values.household.join('-'),
      status: editType === 'add' ? '未領取' : values.status,
      packagerRegistrationTime: dayjs().valueOf(),
      recipient: values.recipient === undefined ? '' : values.recipient,
      peopleHousehold: values.peopleHousehold?.join('-') || null,
      itemPhoto: values.itemPhoto || null,
      receiveTime: editType === 'add' ? '待領取' : values.receiveTime,
      remarks: values.remarks ?? '',
    };
    if (itemImageSrc) {
      newDeposit.itemPhoto = itemImageSrc;
    }

    console.log('newDeposit', newDeposit);
    const service = client.service('deposit');
    if (editType === 'add') {
      await service.create(newDeposit);
      message.success('新增成功');
    }
    if (editType === 'edit') {
      await service.patch(editItem?._id, newDeposit);
      message.success('編輯成功');
    }
    afterEdit();
  };

  useEffect(() => {
    const getVideoDevices = async () => {
      if (navigator?.mediaDevices?.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(
          (device) => device.kind === 'videoinput'
        );
        setVideoDevices(
          videoDevices.map((device) => ({
            label: device.label,
            value: device.deviceId,
          }))
        );
        setSelectedVideoDevice(videoDevices?.[0]?.deviceId || null);
      }
    };
    getVideoDevices();
  }, []);

  useEffect(() => {
    if (open) {
      setShowPage('form');
      setCameraAnimation(false);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      if (editType === 'edit' && editItem) {
        const recipientOptions = getResidentsByHouseholdNumber(
          editItem.household,
          users
        );
        const peopleOptions = getResidentsByHouseholdNumber(
          editItem.peopleHousehold,
          users
        );
        setRecipientOptions(recipientOptions);
        setPeopleOptions(peopleOptions);
        setDepositType(editItem?.type || '寄件');
        form.setFieldsValue(editItem);
        setItemImageSrc(editItem.itemPhoto || null);
      } else {
        clearForm();
      }
    }
  }, [clearForm, editItem, editType, form, open, users]);

  const footer: Record<string, ReactNode> = {
    form: [
      <Button key="cancel" onClick={onCancel}>
        取消
      </Button>,
      <Button key="confirm" type="primary" onClick={form.submit}>
        儲存
      </Button>,
    ],
    camera: [
      <Select
        key="camera-select"
        className={styles['camera-select']}
        options={videoDevices}
        value={selectedVideoDevice}
        onChange={setSelectedVideoDevice}
      />,
      <Button key="take-photo" type="default" onClick={takePhoto}>
        拍照
      </Button>,
      <Button
        key="reset"
        onClick={() => {
          setItemImageSrc(null);
          setCameraAnimation(false);
        }}
      >
        重拍
      </Button>,
      <Button
        key="cancel"
        onClick={() => {
          setShowPage('form');
          setItemImageSrc(editItem?.itemPhoto || null);
        }}
      >
        取消
      </Button>,
      <Button key="confirm" type="primary" onClick={() => setShowPage('form')}>
        確認
      </Button>,
    ],
  };

  const handleFormChange = (
    changedValues: DepositRegistration & {
      household: string[];
      peopleHousehold: string[];
    }
  ) => {
    if ('household' in changedValues) {
      const options = getResidentsByHouseholdNumber(
        changedValues.household,
        users
      );
      form.setFieldValue('recipient', []);
      setRecipientOptions(options);
    }
    if ('peopleHousehold' in changedValues) {
      const options = getResidentsByHouseholdNumber(
        changedValues.peopleHousehold,
        users
      );
      form.setFieldValue('peopleName', []);
      setPeopleOptions(options);
    }
  };

  return (
    <Modal
      open={open}
      title={editType === 'add' ? '新增寄放物品' : '編輯寄放物品'}
      forceRender
      className={styles['modal']}
      onCancel={onCancel}
      footer={footer[showPage]}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          ...editItem,
          household: editItem?.household ? editItem.household : [],
        }}
        onValuesChange={handleFormChange}
        onFinish={handleFinish}
      >
        {showPage === 'form' && (
          <>
            <Item label="物品照片">
              <Space direction="vertical">
                <Image
                  src={itemImageSrc || `${location.pathname}ezcon.png`}
                  width={100}
                  height={100}
                />
                <Button onClick={() => setShowPage('camera')}>開啟相機</Button>
              </Space>
            </Item>
            <Item
              label="類別"
              name="type"
              rules={[{ required: true, message: '請選擇類別' }]}
            >
              <Radio.Group onChange={(e) => setDepositType(e.target.value)}>
                <Radio value="寄件">寄件</Radio>
                <Radio value="收件">收件</Radio>
              </Radio.Group>
            </Item>
            {depositType === '寄件' && (
              <>
                <Item
                  label="寄件人戶號"
                  name="household"
                  rules={[{ required: true, message: '請選擇寄件人戶號' }]}
                >
                  <Cascader
                    options={householdOptions}
                    placeholder="請選擇寄件人戶號"
                  />
                </Item>
                {communityData[0]?.showPackageRecipient && (
                  <Item
                    label="寄件人(選擇後只會有該帳號可以看到資料)"
                    name="recipient"
                  >
                    <Select
                      allowClear
                      placeholder="請選擇寄件人"
                      options={recipientOptions}
                    />
                  </Item>
                )}

                <Item label="收件人戶號" name="peopleHousehold">
                  <Cascader
                    options={householdOptions}
                    placeholder="請選擇收件人戶號"
                  />
                </Item>
                <Item
                  label="收件人"
                  name="peopleName"
                  rules={[{ required: true, message: '請輸入收件人' }]}
                >
                  {peopleHousehold ? (
                    <Select
                      placeholder="請輸入收件人"
                      options={peopleOptions}
                      allowClear
                    />
                  ) : (
                    <Input placeholder="請輸入收件人" allowClear />
                  )}
                </Item>
              </>
            )}
            {depositType === '收件' && (
              <>
                <Item
                  label="寄件人"
                  name="peopleName"
                  rules={[{ required: true, message: '請輸入寄件人' }]}
                >
                  <Input placeholder="請輸入寄件人" allowClear />
                </Item>
                <Item
                  label="收件人戶號"
                  name="household"
                  rules={[{ required: true, message: '請選擇收件人戶號' }]}
                >
                  <Cascader
                    options={householdOptions}
                    placeholder="請選擇收件人戶號"
                  />
                </Item>
                {communityData[0]?.showPackageRecipient && (
                  <Item
                    label="收件人(選擇後只會有該帳號可以看到資料)"
                    name="recipient"
                  >
                    <Select
                      placeholder="請選擇收件人"
                      options={recipientOptions}
                    />
                  </Item>
                )}
              </>
            )}
            <Item
              label="物品名稱"
              name="itemName"
              rules={[{ required: true, message: '請輸入物品名稱' }]}
            >
              <Input placeholder="請輸入物品名稱" allowClear />
            </Item>
            <Item label="備註" name="remarks">
              <TextArea placeholder="請輸入備註" />
            </Item>
          </>
        )}
        {showPage === 'camera' && (
          <>
            <Item label="鏡頭">
              <div className={styles['webcam-container']}>
                {itemImageSrc === null && (
                  <Webcam
                    className="webcam"
                    audio={false}
                    mirrored
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    videoConstraints={{
                      deviceId: selectedVideoDevice ?? undefined,
                      width: 400,
                      height: 400,
                    }}
                  />
                )}
                {itemImageSrc !== null && (
                  <div
                    className={`${
                      cameraAnimation ? styles['item-image-animated'] : ''
                    }`}
                  >
                    <Image src={itemImageSrc} preview={false} />
                  </div>
                )}
              </div>
            </Item>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default EditFormModal;
